import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@foundation/components/ui/card';
import { Badge } from '@foundation/components/ui/badge';
import { Skeleton } from '@foundation/components/ui/skeleton';
import BrandingService from '@foundation/services/brandingService';
import { useUser } from '@foundation/contextapi/UserContext';
import { useWorkspaceContext } from '@foundation/hooks/useWorkspaceContext';
import type { OrganizationBranding } from '@foundation/types/organization';
import {
  Building2,
  MapPin,
  Mail,
  Phone,
  Globe,
  Palette,
  ExternalLink,
  AlertCircle,
} from 'lucide-react';

type OrgContextCardProps = {
  organizationId?: string;
  onBrandingLoad?: (branding: OrganizationBranding | null) => void;
};

type OrgContextData = {
  branding: OrganizationBranding | null;
  workspaceName: string;
};

const OrgContextCard: React.FC<OrgContextCardProps> = ({ organizationId, onBrandingLoad }) => {
  const { user } = useUser();
  const { currentWorkspace, isLoading: isWorkspaceLoading } = useWorkspaceContext();

  const [data, setData] = useState<OrgContextData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const orgId = organizationId || currentWorkspace?.id;
  const workspaceName = currentWorkspace?.name || '';

  const hasLoadedRef = useRef(false);

  const loadOrgData = useCallback(async () => {
    if (!orgId) {
      setIsLoading(false);
      return;
    }

    // Prevent duplicate fetches
    if (hasLoadedRef.current) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      hasLoadedRef.current = true;

      const branding = await BrandingService.getOrganizationBranding(orgId);

      setData({
        branding,
        workspaceName,
      });

      onBrandingLoad?.(branding);
    } catch (err) {
      console.error('Error loading organization data:', err);
      setError('Failed to load organization details');
      hasLoadedRef.current = false; // Reset on error so it can retry
    } finally {
      setIsLoading(false);
    }
  }, [orgId, workspaceName, onBrandingLoad]);

  useEffect(() => {
    if (isWorkspaceLoading) return; // Still waiting for workspace to resolve

    if (!orgId) {
      setIsLoading(false); // Workspace loaded but no org — stop showing skeleton
      return;
    }

    if (!hasLoadedRef.current) {
      loadOrgData();
    }
  }, [isWorkspaceLoading, orgId, loadOrgData]);

  // Loading state with skeleton
  if (isLoading || isWorkspaceLoading) {
    return (
      <Card className="border border-gray-200 bg-white shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <Skeleton className="h-12 w-12 rounded-lg" />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className="border border-red-200 bg-red-50 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-4 w-4" />
            <p className="text-sm">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // No org context available
  if (!orgId) {
    return (
      <Card className="border border-amber-200 bg-amber-50 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-amber-700">
            <AlertCircle className="h-4 w-4" />
            <p className="text-sm">No organization context available. Please select a workspace.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const branding = data?.branding;
  const companyName = branding?.brand_title || workspaceName || 'Not configured';
  const logoUrl = branding?.logo_url
    ? BrandingService.getLogoUrlWithCacheBusting(branding.logo_url)
    : null;
  const primaryColor = branding?.primary_color || null;
  const address = branding?.address as Record<string, string> | null;
  const contactEmail = branding?.contact_email || null;
  const phoneNumber = branding?.phone_number || null;
  const websiteUrl = branding?.website_url || null;
  const tagline = branding?.tagline || null;

  // Build address string from parts
  const addressParts: string[] = [];
  if (address) {
    if (address.street) addressParts.push(address.street);
    if (address.city) addressParts.push(address.city);
    if (address.state) addressParts.push(address.state);
    if (address.postal_code) addressParts.push(address.postal_code);
    if (address.country) addressParts.push(address.country);
  }
  const addressString = addressParts.length > 0 ? addressParts.join(', ') : null;

  return (
    <Card className="border border-gray-200 bg-white shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          {/* Logo or placeholder */}
          <div className="flex-shrink-0">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={`${companyName} logo`}
                className="h-12 w-12 rounded-lg object-contain border border-gray-100"
              />
            ) : (
              <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-gray-400" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <CardTitle className="text-base font-semibold text-gray-900 truncate">
              {companyName}
            </CardTitle>
            {tagline && (
              <CardDescription className="text-xs text-gray-500 mt-0.5 truncate">
                {tagline}
              </CardDescription>
            )}
            <div className="flex items-center gap-2 mt-1.5">
              <Badge variant="secondary" className="text-xs font-normal">
                <Building2 className="h-3 w-3 mr-1" />
                Organization
              </Badge>
              {primaryColor && (
                <div className="flex items-center gap-1">
                  <div
                    className="h-4 w-4 rounded-full border border-gray-200"
                    style={{ backgroundColor: primaryColor }}
                    title={`Primary color: ${primaryColor}`}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0 space-y-2.5">
        {/* Region info from workspace */}
        {currentWorkspace?.name && (
          <InfoRow
            icon={<MapPin className="h-3.5 w-3.5" />}
            label="Workspace"
            value={currentWorkspace.name}
          />
        )}

        {/* Address */}
        <InfoRow
          icon={<MapPin className="h-3.5 w-3.5" />}
          label="Address"
          value={addressString}
          fallback="Not configured"
        />

        {/* Contact email */}
        <InfoRow
          icon={<Mail className="h-3.5 w-3.5" />}
          label="Email"
          value={contactEmail}
          fallback="Not configured"
        />

        {/* Phone */}
        <InfoRow
          icon={<Phone className="h-3.5 w-3.5" />}
          label="Phone"
          value={phoneNumber}
          fallback="Not configured"
        />

        {/* Website */}
        {websiteUrl && (
          <InfoRow
            icon={<Globe className="h-3.5 w-3.5" />}
            label="Website"
            value={websiteUrl}
            isLink
          />
        )}

        {/* Color palette indicator */}
        {branding && (branding.primary_color || branding.secondary_color || branding.accent_color) && (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Palette className="h-3.5 w-3.5 text-gray-400" />
            <span>Brand colors:</span>
            <div className="flex items-center gap-1">
              {branding.primary_color && (
                <div
                  className="h-3.5 w-3.5 rounded-full border border-gray-200"
                  style={{ backgroundColor: branding.primary_color }}
                  title={`Primary: ${branding.primary_color}`}
                />
              )}
              {branding.secondary_color && (
                <div
                  className="h-3.5 w-3.5 rounded-full border border-gray-200"
                  style={{ backgroundColor: branding.secondary_color }}
                  title={`Secondary: ${branding.secondary_color}`}
                />
              )}
              {branding.accent_color && (
                <div
                  className="h-3.5 w-3.5 rounded-full border border-gray-200"
                  style={{ backgroundColor: branding.accent_color }}
                  title={`Accent: ${branding.accent_color}`}
                />
              )}
            </div>
          </div>
        )}
      </CardContent>

      <CardFooter className="pt-2 pb-3">
        <Link
          to="/settings"
          className="inline-flex items-center gap-1.5 text-xs text-[#33b864] hover:text-[#2a9d54] transition-colors font-medium"
        >
          <ExternalLink className="h-3 w-3" />
          Edit in Settings
        </Link>
      </CardFooter>
    </Card>
  );
};

// Helper component for consistent info rows
type InfoRowProps = {
  icon: React.ReactNode;
  label: string;
  value: string | null;
  fallback?: string;
  isLink?: boolean;
};

const InfoRow: React.FC<InfoRowProps> = ({ icon, label, value, fallback = 'Not configured', isLink = false }) => {
  const displayValue = value || fallback;
  const isMissing = !value;

  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="text-gray-400 mt-0.5 flex-shrink-0">{icon}</span>
      <div className="min-w-0 flex-1">
        <span className="text-gray-500">{label}: </span>
        {isLink && value ? (
          <a
            href={value.startsWith('http') ? value : `https://${value}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#33b864] hover:text-[#2a9d54] truncate inline-block max-w-[200px]"
          >
            {value}
          </a>
        ) : (
          <span className={isMissing ? 'text-gray-400 italic' : 'text-gray-700'}>
            {displayValue}
          </span>
        )}
      </div>
    </div>
  );
};

export default OrgContextCard;
